import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, Radio } from "lucide-react";
import { get, fmtDT, fmtDate } from "@/lib/api";
import { StatusBadge, WeakBadge, InterestBar, ConfBadge } from "@/components/badges";
import { useEventDrawer } from "@/components/EventDrawer";

const KPIS = [
  ["today", "Aujourd'hui", "/evenements?period=today", ""], ["tomorrow", "Demain", "/evenements?period=tomorrow", ""],
  ["week", "Cette semaine", "/evenements?period=7d", ""], ["new", "Nouveaux (48 h)", "/evenements?is_new=1", "emerald"],
  ["modified", "Modifiés", "/evenements?status=MODIFIÉ", "amber"], ["cancelled", "Annulés", "/evenements?status=ANNULÉ", "rose"],
  ["to_verify", "À vérifier", "/evenements?conf=verify", "yellow"], ["weak_signals", "Signaux faibles", "/signaux", "indigo"],
  ["economic", "Économiques / pro", "/evenements?category=ECO", "blue"], ["new_sources", "Nouvelles sources (7 j)", "/sources?recent=7", ""],
  ["error_sources", "Sources en erreur", "/sources?status=erreur", "rose"],
];
const ACCENT = { emerald: "border-l-emerald-500", amber: "border-l-amber-500", rose: "border-l-rose-600", yellow: "border-l-yellow-500", indigo: "border-l-indigo-600", blue: "border-l-blue-700", "": "border-l-slate-300 dark:border-l-slate-600" };

const MiniList = ({ title, items, link, testId, empty = "Rien à signaler." }) => {
  const { open } = useEventDrawer();
  return (
    <div className="panel" data-testid={testId}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 dark:border-slate-800">
        <h3 className="font-display text-sm font-bold">{title}</h3>
        {link && <Link to={link} className="text-[11px] text-slate-500 hover:text-slate-900 dark:hover:text-white inline-flex items-center gap-1">Tout voir<ArrowRight className="h-3 w-3" /></Link>}
      </div>
      <ul>
        {(items || []).map((e) => (
          <li key={e.id} onClick={() => open(e.id)} className="px-3 py-2 border-b last:border-0 border-slate-100 dark:border-slate-800 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors duration-150">
            <div className="flex items-center gap-1.5 mb-0.5"><StatusBadge status={e.status} />{e.weak_signal && <WeakBadge />}<span className="font-mono text-[10px] text-slate-500 ml-auto">{fmtDate(e.next_date)} {e.start_time || ""}</span></div>
            <div className="text-xs font-medium leading-snug">{e.title}</div>
            <div className="flex items-center justify-between mt-0.5"><span className="text-[11px] text-slate-500">{e.commune_name}{e.venue_name ? ` · ${e.venue_name}` : ""}</span>
              <span className="flex items-center gap-2"><InterestBar value={e.interest} /><ConfBadge value={e.confidence} /></span></div>
          </li>
        ))}
        {(!items || items.length === 0) && <li className="px-3 py-6 text-center text-xs text-slate-400">{empty}</li>}
      </ul>
    </div>
  );
};

export default function Dashboard() {
  const { data } = useQuery({ queryKey: ["dashboard"], queryFn: () => get("/dashboard"), refetchInterval: 120000 });
  const { open } = useEventDrawer();
  if (!data) return <div className="p-6 text-xs text-slate-500">Chargement du tableau de bord…</div>;
  const { kpi, delta, lists } = data;
  return (
    <div className="p-4 lg:p-6 space-y-5 max-w-[1700px] mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-3 fade-up">
        <div>
          <div className="overline">Radar territorial · {kpi.communes} commune(s) surveillée(s) · {kpi.sources_total} sources</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Que se passe-t-il sur le territoire ?</h1>
        </div>
        <div className="text-[11px] font-mono text-slate-500">Dernière collecte : {fmtDT(data.last_run?.ended_at)}</div>
      </div>

      {kpi.communes === 0 && (
        <div className="panel p-5 border-l-4 border-l-blue-700 fade-up" data-testid="onboarding-card">
          <h2 className="font-display font-bold text-base flex items-center gap-2"><Building2 className="h-4 w-4" />Commencez par définir votre territoire</h2>
          <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">Ajoutez des communes depuis l'annuaire officiel (geo.api.gouv.fr), puis lancez une exploration : le radar découvrira automatiquement les agendas et sources du territoire.</p>
          <Link to="/communes" className="inline-flex mt-3 text-xs font-semibold text-blue-700 hover:underline" data-testid="onboarding-link">Ouvrir l'annuaire des communes →</Link>
        </div>
      )}

      <div data-testid="delta-banner" className="panel px-4 py-3 border-l-4 border-l-emerald-500 flex flex-wrap items-center gap-2 fade-up">
        <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
        <span className="text-sm">Depuis votre dernière consultation ({fmtDT(delta.since)}) : <b className="font-mono">{delta.new}</b> nouveaux événements et <b className="font-mono">{delta.modified}</b> modifications.</span>
        {kpi.contradictions > 0 && <Link to="/evenements?contradiction=1" className="text-xs text-rose-700 font-semibold ml-2">{kpi.contradictions} contradiction(s) à arbitrer →</Link>}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-11 gap-2">
        {KPIS.map(([k, label, to, accent], i) => (
          <Link key={k} to={to} data-testid={`kpi-${k}`} style={{ animationDelay: `${i * 30}ms` }} className={`panel fade-up border-l-4 ${ACCENT[accent]} px-3 py-2.5 hover:-translate-y-0.5 transition-transform duration-150`}>
            <div className="overline !text-[9px] leading-tight min-h-[22px]">{label}</div>
            <div className="font-mono text-2xl font-semibold tabular-nums mt-1">{kpi[k]}</div>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 [&>*]:min-w-0 lg:grid-cols-3 gap-4">
        <MiniList title="Aujourd'hui" items={lists.today} link="/evenements?period=today" testId="list-today" empty="Aucun événement détecté aujourd'hui." />
        <MiniList title="Nouveaux depuis votre dernière visite" items={lists.new} link="/evenements?is_new=1" testId="list-new" />
        <MiniList title="Signaux faibles" items={lists.weak} link="/signaux" testId="list-weak" />
        <MiniList title="Économie & entreprises" items={lists.economic} link="/evenements?category=ECO" testId="list-economic" />
        <MiniList title="Annulés / reportés" items={lists.cancelled} link="/evenements?status=ANNULÉ&status=REPORTÉ" testId="list-cancelled" />
        <MiniList title="À vérifier" items={lists.to_verify} link="/evenements?conf=verify" testId="list-to-verify" />
      </div>

      <div className="grid grid-cols-1 [&>*]:min-w-0 lg:grid-cols-3 gap-4">
        <div className="panel" data-testid="recent-changes">
          <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-800 font-display text-sm font-bold">Derniers changements détectés</div>
          <ul>{data.changes.map((h) => (
            <li key={h.id} onClick={() => open(h.event_id)} className="px-3 py-1.5 border-b last:border-0 border-slate-100 dark:border-slate-800 text-xs cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50">
              <span className="font-mono text-[10px] text-slate-500">{fmtDT(h.at)}</span> <b>{h.event_title}</b> — {h.message}
            </li>))}
            {data.changes.length === 0 && <li className="px-3 py-6 text-center text-xs text-slate-400">Aucun changement.</li>}
          </ul>
        </div>
        <div className="panel" data-testid="new-sources-panel">
          <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-800 font-display text-sm font-bold flex items-center gap-1.5"><Radio className="h-3.5 w-3.5" />Nouvelles sources découvertes</div>
          <ul>{data.new_sources.map((s) => (
            <li key={s.id} className="px-3 py-1.5 border-b last:border-0 border-slate-100 dark:border-slate-800 text-xs">
              <div className="font-medium truncate">{s.name}</div><div className="text-[10px] text-slate-500 truncate">{s.discovered_by} · {fmtDT(s.discovered_at)}</div>
            </li>))}</ul>
        </div>
        <div className="panel" data-testid="error-sources-panel">
          <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-800 font-display text-sm font-bold">Sources en erreur</div>
          <ul>{data.error_sources.map((s) => (
            <li key={s.id} className="px-3 py-1.5 border-b last:border-0 border-slate-100 dark:border-slate-800 text-xs"><div className="font-medium">{s.name}</div><div className="text-[10px] text-rose-600 truncate">{s.last_error}</div></li>))}
            {data.error_sources.length === 0 && <li className="px-3 py-6 text-center text-xs text-slate-400">Aucune source en erreur.</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}
